import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertCircle, Lock } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { getErrorMessage, getValidationErrors } from '../../services/api';
import { authService } from '../../services/auth.service';
import { oauthService, ConsentRequestView } from '../../services/oauth.service';

/**
 * The OAuth consent screen — the only page in the MCP feature a customer ever sees.
 *
 * ── Why sign-in happens HERE rather than via /login ──────────────────────────
 * The obvious route is to bounce a signed-out user to `/login?redirect=…`. It does not work:
 * `AuthContext.login` navigates to `/dashboard` (or `/super-admin`) unconditionally and ignores
 * any redirect parameter, so the user would land on the dashboard and the connector waiting on
 * the callback would simply hang with no error.
 *
 * Rather than change the navigation every existing user's login depends on, this page signs in
 * against `authService.login` directly — which sets the same cookies without navigating — and
 * then re-reads the request in place. The result is one screen, which is also the flow that was
 * specified: sign in if needed, then consent.
 *
 * The three credential fields are duplicated from Login.tsx as a consequence. If a THIRD login
 * surface ever appears, extract them into a shared component then; two is not yet worth
 * destabilising the primary login path for.
 *
 * ── What the user is being asked ─────────────────────────────────────────────
 * A grant is per-account, not per-person, so the screen names the exact email and organisation
 * it would be made against. Someone signed into two tenants in one browser otherwise has no way
 * to tell which one they are connecting.
 *
 * `client.name` and `client.uri` are values the client chose at registration. They are rendered
 * as text, never as markup, and the URI is shown as plain text rather than an anchor — a
 * consent screen is exactly where a convincing name and a hostile link do the most damage.
 */

/** Human wording for the scopes this server issues. Unknown scopes fall back to their raw id. */
const SCOPE_LABELS: Record<string, string> = {
  'okrhub:read': 'Read your OKRs, key results, check-ins and organisation statistics',
};

const describeScope = (scope: string): string => SCOPE_LABELS[scope] ?? scope;

export const OAuthConsent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const request = searchParams.get('request') ?? '';

  const [view, setView] = useState<ConsentRequestView | null>(null);
  const [loadError, setLoadError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDeciding, setIsDeciding] = useState(false);

  // Sign-in form state, used only when the visitor has no session.
  const [formData, setFormData] = useState({ email: '', password: '', organizationName: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [authError, setAuthError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);

  const load = useCallback(async () => {
    if (!request) {
      setLoadError('This link is missing its authorization request. Start the connection again from your client.');
      setIsLoading(false);
      return;
    }
    try {
      setView(await oauthService.getConsentRequest(request));
      setLoadError('');
    } catch (error) {
      setLoadError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!formData.organizationName) nextErrors.organizationName = 'Organization is required';
    if (!formData.email) nextErrors.email = 'Email is required';
    if (!formData.password) nextErrors.password = 'Password is required';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSigningIn(true);
    setAuthError('');
    try {
      await authService.login(formData);
      // Deliberately no navigation — re-read the same request, now as an identified user.
      setIsLoading(true);
      await load();
    } catch (error) {
      const validationErrors = getValidationErrors(error);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...validationErrors }));
      }
      setAuthError(getErrorMessage(error));
    } finally {
      setIsSigningIn(false);
    }
  };

  const decide = async (decision: 'allow' | 'deny') => {
    setIsDeciding(true);
    try {
      const redirectUrl = await oauthService.submitDecision(request, decision);
      // Full navigation, not react-router: the destination is the client's own callback on
      // another origin, and it must land in the address bar.
      window.location.href = redirectUrl;
    } catch (error) {
      setLoadError(getErrorMessage(error));
      setIsDeciding(false);
    }
  };

  const clientName = view?.client.name?.trim() || 'An application';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Card padding="lg">
          {isLoading ? (
            <div className="py-12 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : loadError ? (
            <div className="text-center py-4">
              <AlertCircle className="w-10 h-10 text-danger-500 mx-auto mb-4" aria-hidden="true" />
              <h1 className="text-lg font-semibold text-gray-900 mb-2">
                This request can&rsquo;t be completed
              </h1>
              <p className="text-sm text-gray-600">{loadError}</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-6 h-6 text-primary-600" aria-hidden="true" />
                </div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {clientName} wants to access OKR Hub
                </h1>
                {view?.client.uri && (
                  // Plain text, never a link: a consent screen is the worst place to offer a
                  // click-through to an address the requester chose.
                  <p className="text-xs text-gray-500 mt-1 break-all">{view.client.uri}</p>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 mb-6">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">
                  It will be able to
                </p>
                <ul className="space-y-2">
                  {(view?.scopes.length ? view.scopes : ['okrhub:read']).map(scope => (
                    <li key={scope} className="flex gap-2 text-sm text-gray-700">
                      <Lock className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>{describeScope(scope)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 mt-3">
                  It cannot create, change or delete anything.
                </p>
              </div>

              {view?.authenticated ? (
                <>
                  <div className="text-sm text-gray-600 mb-6">
                    Signed in as{' '}
                    <span className="font-medium text-gray-900">{view.user?.email}</span>
                    {view.user?.organizationName && (
                      <>
                        {' '}in{' '}
                        <span className="font-medium text-gray-900">
                          {view.user.organizationName}
                        </span>
                      </>
                    )}
                    .
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      fullWidth
                      onClick={() => decide('deny')}
                      disabled={isDeciding}
                    >
                      Deny
                    </Button>
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => decide('allow')}
                      isLoading={isDeciding}
                    >
                      Allow
                    </Button>
                  </div>
                </>
              ) : (
                <form onSubmit={handleSignIn} className="space-y-4">
                  <p className="text-sm text-gray-600">Sign in to continue.</p>
                  {authError && (
                    <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm">
                      {authError}
                    </div>
                  )}
                  <Input
                    label="Organization"
                    value={formData.organizationName}
                    onChange={e =>
                      setFormData({ ...formData, organizationName: e.target.value })
                    }
                    error={errors.organizationName}
                    autoComplete="organization"
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    error={errors.email}
                    autoComplete="username"
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    error={errors.password}
                    autoComplete="current-password"
                  />
                  <Button type="submit" variant="primary" fullWidth isLoading={isSigningIn}>
                    Sign in
                  </Button>
                </form>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default OAuthConsent;
